import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  api,
  type CreateVendorContactResponse,
  type CreateVendorSpecializationResponse,
  type PatchVendorResponse,
  type PatchVendorContactResponse,
  type Vendor,
  type VendorScorecardResponse,
  type VendorSpecializationKind,
  type VendorSpecializationValuesResponse,
} from '@/lib/api';

interface Props {
  publicId: string;
}

const SPEC_KIND_LABEL: Record<VendorSpecializationKind, string> = {
  aircraft: 'Aircraft',
  part_category: 'Part category',
  region: 'Region',
  engine: 'Engine',
};

const CURRENCY_SYMBOL: Record<string, string> = {
  USD: '$', EUR: '€', GBP: '£', CAD: 'CA$', AUD: 'A$',
  JPY: '¥', CHF: 'CHF ', AED: 'AED ', SGD: 'S$',
};

function fmtMoney(cents: number, currency: string): string {
  const sym = CURRENCY_SYMBOL[currency] ?? `${currency} `;
  if (currency === 'JPY') return `${sym}${Math.round(cents / 100).toLocaleString('en-US')}`;
  return `${sym}${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Exactly the nine fields PATCH /api/vendors/:publicId accepts.
 *
 * Deliberately not a subset of Vendor: the counters (totalQuotesReceived,
 * winRate, avgResponseHours, ...) are derived from quote history and are
 * recomputed, never typed. Offering them as inputs would invite someone to
 * "correct" a number that the next recompute overwrites.
 *
 * emailDomain is also absent — the server sets it from the vendor's quote
 * traffic and does not accept it on PATCH.
 */
interface VendorDraft {
  legalName: string;
  displayName: string;
  partsbaseProfileUrl: string;
  reliabilityRating: string;
  aogCapable: boolean;
  brokerFriendly: boolean;
  paymentTerms: string;
  preferredCurrency: string;
  notes: string;
}

const EMPTY_DRAFT: VendorDraft = {
  legalName: '', displayName: '', partsbaseProfileUrl: '', reliabilityRating: '',
  aogCapable: false, brokerFriendly: true, paymentTerms: '', preferredCurrency: '', notes: '',
};

const seedDraft = (v: Vendor): VendorDraft => ({
  legalName: v.legalName ?? '',
  displayName: v.displayName ?? '',
  partsbaseProfileUrl: v.partsbaseProfileUrl ?? '',
  reliabilityRating: v.reliabilityRating == null ? '' : String(v.reliabilityRating),
  aogCapable: Boolean(v.aogCapable),
  brokerFriendly: Boolean(v.brokerFriendly),
  paymentTerms: v.paymentTerms ?? '',
  preferredCurrency: v.preferredCurrency ?? '',
  notes: v.notes ?? '',
});

/** Diff-only, matching customer-detail.tsx: send what changed and nothing
 *  else, so a no-op save writes no audit row and does not move updated_at. */
function vendorDiff(draft: VendorDraft, v: Vendor): Record<string, unknown> {
  const b: Record<string, unknown> = {};
  const txt = (k: 'legalName' | 'displayName' | 'partsbaseProfileUrl' | 'paymentTerms'
                 | 'preferredCurrency' | 'notes', cur: string | null) => {
    const next = draft[k].trim() === '' ? null : draft[k].trim();
    if (next !== (cur ?? null)) b[k] = next;
  };
  // legalName is NOT NULL server-side; an empty box means "unchanged", not "clear".
  if (draft.legalName.trim() && draft.legalName.trim() !== v.legalName) {
    b.legalName = draft.legalName.trim();
  }
  txt('displayName', v.displayName);
  txt('partsbaseProfileUrl', v.partsbaseProfileUrl);
  txt('paymentTerms', v.paymentTerms);
  txt('preferredCurrency', v.preferredCurrency);
  txt('notes', v.notes);

  const rating = draft.reliabilityRating.trim();
  const ratingVal = rating === '' ? null : Number(rating);
  if (ratingVal !== (v.reliabilityRating ?? null)) b.reliabilityRating = ratingVal;

  if (draft.aogCapable !== Boolean(v.aogCapable)) b.aogCapable = draft.aogCapable;
  if (draft.brokerFriendly !== Boolean(v.brokerFriendly)) b.brokerFriendly = draft.brokerFriendly;
  return b;
}

export function VendorDetail({ publicId }: Props) {
  const { getToken } = useAuth();
  const qc = useQueryClient();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['vendor', publicId],
    queryFn: () =>
      api<VendorScorecardResponse>(`/api/vendors/${publicId}`, {
        tokenGetter: () => getToken(),
      }),
    staleTime: 10_000,
  });

  const [editingNotes, setEditingNotes] = useState<string | null>(null);
  const [showAddContact, setShowAddContact] = useState(false);
  const [contactDraft, setContactDraft] = useState({ fullName: '', email: '', phone: '', role: '' });
  const [showAddSpec, setShowAddSpec] = useState(false);
  const [specDraft, setSpecDraft] = useState<{ kind: VendorSpecializationKind; value: string }>({
    kind: 'aircraft',
    value: '',
  });

  // Autocomplete suggestions for the tag-value input, scoped to the chosen kind.
  // Keeps tag values consistent across vendors so the matrix groups cleanly.
  const { data: specValues } = useQuery({
    queryKey: ['vendor-spec-values', specDraft.kind],
    queryFn: () =>
      api<VendorSpecializationValuesResponse>(
        `/api/vendor-specializations/values?kind=${encodeURIComponent(specDraft.kind)}`,
        { tokenGetter: () => getToken() },
      ),
    enabled: showAddSpec,
    staleTime: 60_000,
  });

  const errMsg = (e: unknown) => (e as { message?: string })?.message ?? 'Something went wrong.';

  // Vendor identity editing. PATCH /api/vendors/:id has accepted these nine
  // fields since the route was written; nothing ever sent more than `notes`,
  // so every one of them was read-only in practice.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<VendorDraft>(EMPTY_DRAFT);
  const [editError, setEditError] = useState<string | null>(null);

  // Contact editing. PATCH /api/vendor-contacts/:publicId has existed all
  // along and the client only ever created and deleted — so fixing a typo in a
  // contact's email meant deleting them and typing it all again.
  const [editingContact, setEditingContact] = useState<string | null>(null);
  // Separate from the add-contact draft above: opening an edit must not clobber
  // a half-typed new contact.
  const [editContactDraft, setEditContactDraft] =
    useState({ fullName: '', email: '', phone: '', role: '' });

  const patchContact = useMutation({
    mutationFn: async (args: { id: string; patch: Record<string, unknown> }) =>
      api<PatchVendorContactResponse>(`/api/vendor-contacts/${args.id}`, {
        method: 'PATCH',
        tokenGetter: () => getToken(),
        body: JSON.stringify(args.patch),
      }),
    onSuccess: () => {
      setEditingContact(null);
      qc.invalidateQueries({ queryKey: ['vendor', publicId] });
    },
  });

  const patchVendor = useMutation({
    mutationFn: async (patch: Record<string, unknown>) =>
      api<PatchVendorResponse>(`/api/vendors/${publicId}`, {
        method: 'PATCH',
        tokenGetter: () => getToken(),
        body: JSON.stringify(patch),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vendor', publicId] });
      qc.invalidateQueries({ queryKey: ['vendors'] });
    },
  });

  const createContact = useMutation({
    mutationFn: async () =>
      api<CreateVendorContactResponse>('/api/vendor-contacts', {
        method: 'POST',
        tokenGetter: () => getToken(),
        body: JSON.stringify({
          vendorPublicId: publicId,
          fullName: contactDraft.fullName || undefined,
          email: contactDraft.email || undefined,
          phone: contactDraft.phone || undefined,
          role: contactDraft.role || undefined,
        }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vendor', publicId] });
      setContactDraft({ fullName: '', email: '', phone: '', role: '' });
      setShowAddContact(false);
    },
  });

  const deleteContact = useMutation({
    mutationFn: async (contactId: string) =>
      api<void>(`/api/vendor-contacts/${contactId}`, {
        method: 'DELETE',
        tokenGetter: () => getToken(),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vendor', publicId] }),
  });

  const createSpec = useMutation({
    mutationFn: async () =>
      api<CreateVendorSpecializationResponse>('/api/vendor-specializations', {
        method: 'POST',
        tokenGetter: () => getToken(),
        body: JSON.stringify({
          vendorPublicId: publicId,
          kind: specDraft.kind,
          value: specDraft.value,
        }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vendor', publicId] });
      setSpecDraft({ kind: specDraft.kind, value: '' });
      setShowAddSpec(false);
    },
  });

  const deleteSpec = useMutation({
    mutationFn: async (specId: string) =>
      api<void>(`/api/vendor-specializations/${specId}`, {
        method: 'DELETE',
        tokenGetter: () => getToken(),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vendor', publicId] }),
  });

  const recompute = useMutation({
    mutationFn: async () =>
      api<PatchVendorResponse>(`/api/vendors/${publicId}/recompute`, {
        method: 'POST',
        tokenGetter: () => getToken(),
        body: JSON.stringify({}),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vendor', publicId] }),
  });

  if (isLoading) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  if (isError) {
    return (
      <div className="p-6 text-sm text-destructive">
        {(error as { message?: string })?.message ?? 'Failed to load vendor'}
      </div>
    );
  }
  if (!data) return null;

  const { vendor, contacts, specializations, recentActivity, topPartNumbers } = data;

  return (
    <div className="h-full overflow-auto">
      <div className="px-6 py-5 border-b border-border">
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <div>
            <div className="text-lg font-semibold">{vendor.displayName ?? vendor.legalName}</div>
            {vendor.displayName && vendor.displayName !== vendor.legalName && (
              <div className="text-xs text-muted-foreground">{vendor.legalName}</div>
            )}
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            {!editing && (
              <button
                onClick={() => {
                  setDraft(seedDraft(vendor));
                  setEditError(null);
                  setEditing(true);
                }}
                className="text-xs font-medium text-primary hover:underline"
                title="Edit this vendor's name, terms and rating"
              >
                Edit
              </button>
            )}
            {vendor.aogCapable && (
              <span className="px-2 py-0.5 text-xs font-bold rounded bg-amber-100 text-amber-900">AOG</span>
            )}
            {!vendor.brokerFriendly && (
              <span className="px-2 py-0.5 text-xs rounded bg-red-100 text-red-900">not broker-friendly</span>
            )}
            {vendor.preferredCurrency && (
              <span className="px-2 py-0.5 text-xs rounded bg-muted">{vendor.preferredCurrency}</span>
            )}
            {vendor.reliabilityRating && (
              <span className="px-2 py-0.5 text-xs rounded bg-muted">★ {vendor.reliabilityRating}/5</span>
            )}
          </div>
        </div>

        {editing && (
          <div className="mt-4 rounded border border-border bg-surface-muted/40 p-3 text-xs space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-[11px] font-medium text-foreground-muted mb-1">
                  Legal name
                </span>
                <input
                  className="w-full rounded border border-border bg-surface px-2 py-1 text-xs"
                  value={draft.legalName}
                  onChange={(e) => setDraft({ ...draft, legalName: e.target.value })}
                />
                <span className="block text-[10px] text-foreground-muted mt-0.5">
                  Appears on vendor POs. Renaming re-derives the dedup key, so a clash with an
                  existing vendor is refused — merge instead.
                </span>
              </label>
              <label className="block">
                <span className="block text-[11px] font-medium text-foreground-muted mb-1">
                  Display name
                </span>
                <input
                  className="w-full rounded border border-border bg-surface px-2 py-1 text-xs"
                  value={draft.displayName}
                  onChange={(e) => setDraft({ ...draft, displayName: e.target.value })}
                />
              </label>
              <label className="block">
                <span className="block text-[11px] font-medium text-foreground-muted mb-1">
                  Payment terms
                </span>
                <input
                  className="w-full rounded border border-border bg-surface px-2 py-1 text-xs"
                  value={draft.paymentTerms}
                  onChange={(e) => setDraft({ ...draft, paymentTerms: e.target.value })}
                />
              </label>
              <label className="block">
                <span className="block text-[11px] font-medium text-foreground-muted mb-1">
                  Preferred currency
                </span>
                <input
                  className="w-full rounded border border-border bg-surface px-2 py-1 text-xs"
                  maxLength={3}
                  value={draft.preferredCurrency}
                  onChange={(e) =>
                    setDraft({ ...draft, preferredCurrency: e.target.value.toUpperCase() })}
                />
              </label>
              <label className="block">
                <span className="block text-[11px] font-medium text-foreground-muted mb-1">
                  Reliability rating (1–5)
                </span>
                <input
                  className="w-full rounded border border-border bg-surface px-2 py-1 text-xs"
                  inputMode="numeric"
                  value={draft.reliabilityRating}
                  onChange={(e) => setDraft({ ...draft, reliabilityRating: e.target.value })}
                />
              </label>
              <label className="block">
                <span className="block text-[11px] font-medium text-foreground-muted mb-1">
                  PartsBase profile URL
                </span>
                <input
                  className="w-full rounded border border-border bg-surface px-2 py-1 text-xs"
                  value={draft.partsbaseProfileUrl}
                  onChange={(e) => setDraft({ ...draft, partsbaseProfileUrl: e.target.value })}
                />
              </label>
            </div>

            <div className="flex gap-4">
              <label className="flex items-center gap-1.5">
                <input type="checkbox" checked={draft.aogCapable}
                  onChange={(e) => setDraft({ ...draft, aogCapable: e.target.checked })} />
                <span>AOG capable</span>
              </label>
              <label className="flex items-center gap-1.5">
                <input type="checkbox" checked={draft.brokerFriendly}
                  onChange={(e) => setDraft({ ...draft, brokerFriendly: e.target.checked })} />
                <span>Broker friendly</span>
              </label>
            </div>

            {editError && <div className="text-danger">{editError}</div>}

            <div className="flex items-center justify-between border-t border-border pt-2">
              <span className="text-foreground-muted">
                {Object.keys(vendorDiff(draft, vendor)).length === 0
                  ? 'No changes yet'
                  : `${Object.keys(vendorDiff(draft, vendor)).length} field(s) changed`}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => { setEditing(false); setEditError(null); }}
                  className="btn-ghost text-xs px-3 py-1"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const body = vendorDiff(draft, vendor);
                    if (Object.keys(body).length === 0) { setEditing(false); return; }
                    setEditError(null);
                    patchVendor.mutate(body, {
                      onSuccess: () => setEditing(false),
                      // A rename that collides with another vendor comes back 409
                      // telling you to merge. Surface it rather than swallowing it.
                      onError: (e: unknown) => setEditError(errMsg(e)),
                    });
                  }}
                  disabled={patchVendor.isPending}
                  className="btn-primary text-xs px-3 py-1 disabled:opacity-50"
                >
                  {patchVendor.isPending ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Scorecard counters */}
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Quotes received" value={String(vendor.totalQuotesReceived)} />
          <Stat label="Wins" value={String(vendor.totalWins)} />
          <Stat label="Win rate" value={`${vendor.winRate}%`} />
          <Stat
            label="Avg response"
            value={vendor.avgResponseHours ? `${Number(vendor.avgResponseHours).toFixed(1)}h` : '—'}
          />
          <Stat
            label="Avg lead time"
            value={vendor.avgLeadTimeDays ? `${Number(vendor.avgLeadTimeDays).toFixed(1)}d` : '—'}
          />
          <Stat
            label="Last quote"
            value={vendor.lastQuotedAt ? new Date(vendor.lastQuotedAt).toLocaleDateString() : '—'}
          />
          <Stat
            label="Last won"
            value={vendor.lastWonAt ? new Date(vendor.lastWonAt).toLocaleDateString() : '—'}
          />
          <Stat
            label="Payment terms"
            value={vendor.paymentTerms ?? '—'}
          />
        </div>

        <div className="mt-3 flex gap-2 flex-wrap">
          <button
            onClick={() => recompute.mutate()}
            disabled={recompute.isPending}
            className="px-2.5 py-1 text-xs border border-border rounded hover:bg-muted disabled:opacity-50"
          >
            {recompute.isPending ? 'Recomputing…' : 'Recompute counters'}
          </button>
          {vendor.partsbaseProfileUrl && (
            <a
              href={vendor.partsbaseProfileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-2.5 py-1 text-xs border border-border rounded hover:bg-muted"
            >
              PartsBase profile ↗
            </a>
          )}
        </div>
      </div>

      <div className="px-6 py-4 grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Specializations */}
        <section>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs uppercase tracking-wide text-muted-foreground">
              Specializations ({specializations.length})
            </h3>
            <button
              onClick={() => {
                createSpec.reset();
                setShowAddSpec((s) => !s);
              }}
              className="text-xs font-medium text-primary hover:underline"
            >
              + Add
            </button>
          </div>
          {showAddSpec && (
            <div className="mb-2">
              <div className="flex gap-2">
                <select
                  value={specDraft.kind}
                  onChange={(e) =>
                    setSpecDraft({ ...specDraft, kind: e.target.value as VendorSpecializationKind })
                  }
                  className="px-2 py-1 text-xs bg-background border border-border rounded"
                >
                  {(Object.keys(SPEC_KIND_LABEL) as VendorSpecializationKind[]).map((k) => (
                    <option key={k} value={k}>{SPEC_KIND_LABEL[k]}</option>
                  ))}
                </select>
                <input
                  value={specDraft.value}
                  onChange={(e) => setSpecDraft({ ...specDraft, value: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && specDraft.value.trim() && !createSpec.isPending) {
                      createSpec.mutate();
                    }
                  }}
                  list="spec-value-suggestions"
                  placeholder="Bell 412, avionics, EMEA…"
                  className="flex-1 px-2 py-1 text-xs bg-background border border-border rounded"
                />
                <datalist id="spec-value-suggestions">
                  {(specValues?.values ?? []).map((v) => (
                    <option key={v} value={v} />
                  ))}
                </datalist>
                <button
                  onClick={() => createSpec.mutate()}
                  disabled={!specDraft.value.trim() || createSpec.isPending}
                  className="px-2.5 py-1 text-xs font-medium bg-primary text-primary-foreground rounded disabled:opacity-50"
                >
                  Save
                </button>
              </div>
              {createSpec.isError && (
                <div className="mt-1 text-xs text-destructive">{errMsg(createSpec.error)}</div>
              )}
            </div>
          )}
          {specializations.length === 0 ? (
            <div className="text-xs italic text-muted-foreground">No tags yet.</div>
          ) : (
            <ul className="flex flex-wrap gap-1.5">
              {specializations.map((s) => (
                <li
                  key={s.id}
                  className="px-2 py-0.5 text-xs rounded bg-muted flex items-center gap-1.5"
                >
                  <span className="text-muted-foreground">{SPEC_KIND_LABEL[s.kind as VendorSpecializationKind] ?? s.kind}:</span>
                  <span className="font-medium">{s.value}</span>
                  {s.source === 'derived_from_quotes' && (
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">auto</span>
                  )}
                  <button
                    onClick={() => deleteSpec.mutate(s.id)}
                    className="text-muted-foreground hover:text-destructive ml-0.5"
                    aria-label="Remove tag"
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Contacts */}
        <section>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs uppercase tracking-wide text-muted-foreground">
              Contacts ({contacts.length})
            </h3>
            <button
              onClick={() => {
                createContact.reset();
                setShowAddContact((s) => !s);
              }}
              className="text-xs font-medium text-primary hover:underline"
            >
              + Add
            </button>
          </div>
          {showAddContact && (
            <div className="mb-2 grid grid-cols-2 gap-2 border border-border rounded p-2 bg-background">
              <input
                value={contactDraft.fullName}
                onChange={(e) => setContactDraft({ ...contactDraft, fullName: e.target.value })}
                placeholder="Full name"
                className="px-2 py-1 text-xs bg-background border border-border rounded"
              />
              <input
                value={contactDraft.email}
                onChange={(e) => setContactDraft({ ...contactDraft, email: e.target.value })}
                placeholder="email@vendor.com"
                className="px-2 py-1 text-xs bg-background border border-border rounded font-mono"
              />
              <input
                value={contactDraft.phone}
                onChange={(e) => setContactDraft({ ...contactDraft, phone: e.target.value })}
                placeholder="Phone"
                className="px-2 py-1 text-xs bg-background border border-border rounded font-mono"
              />
              <input
                value={contactDraft.role}
                onChange={(e) => setContactDraft({ ...contactDraft, role: e.target.value })}
                placeholder="Role (sales, logistics, etc.)"
                className="px-2 py-1 text-xs bg-background border border-border rounded"
              />
              <button
                onClick={() => createContact.mutate()}
                disabled={
                  createContact.isPending ||
                  (!contactDraft.fullName.trim() && !contactDraft.email.trim())
                }
                title={
                  !contactDraft.fullName.trim() && !contactDraft.email.trim()
                    ? 'Enter at least a name or an email'
                    : undefined
                }
                className="col-span-2 px-2.5 py-1 text-xs font-medium bg-primary text-primary-foreground rounded disabled:opacity-50"
              >
                {createContact.isPending ? 'Saving…' : 'Save contact'}
              </button>
              {createContact.isError && (
                <div className="col-span-2 text-xs text-destructive">{errMsg(createContact.error)}</div>
              )}
            </div>
          )}
          {contacts.length === 0 ? (
            <div className="text-xs italic text-muted-foreground">No contacts yet.</div>
          ) : (
            <ul className="space-y-1.5">
              {contacts.map((c) => (
                <li key={c.id} className="text-xs border border-border rounded p-2 bg-background">
                  {editingContact === c.id ? (
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          className="w-full rounded border border-border bg-surface px-2 py-1"
                          placeholder="Full name" value={editContactDraft.fullName}
                          onChange={(e) => setEditContactDraft({ ...editContactDraft, fullName: e.target.value })} />
                        <input
                          className="w-full rounded border border-border bg-surface px-2 py-1"
                          placeholder="Role" value={editContactDraft.role}
                          onChange={(e) => setEditContactDraft({ ...editContactDraft, role: e.target.value })} />
                        <input
                          className="w-full rounded border border-border bg-surface px-2 py-1"
                          placeholder="Email" value={editContactDraft.email}
                          onChange={(e) => setEditContactDraft({ ...editContactDraft, email: e.target.value })} />
                        <input
                          className="w-full rounded border border-border bg-surface px-2 py-1"
                          placeholder="Phone" value={editContactDraft.phone}
                          onChange={(e) => setEditContactDraft({ ...editContactDraft, phone: e.target.value })} />
                      </div>
                      <div className="flex justify-end gap-2">
                        <button onClick={() => setEditingContact(null)}
                          className="btn-ghost text-xs px-2 py-0.5">Cancel</button>
                        <button
                          onClick={() => {
                            // Diff-only, same rule as the vendor panel above.
                            const patch: Record<string, unknown> = {};
                            const t = (k: 'fullName' | 'email' | 'phone' | 'role', cur: string | null) => {
                              const v = editContactDraft[k].trim() === '' ? null : editContactDraft[k].trim();
                              if (v !== (cur ?? null)) patch[k] = v;
                            };
                            t('fullName', c.fullName); t('email', c.email);
                            t('phone', c.phone); t('role', c.role);
                            if (Object.keys(patch).length === 0) { setEditingContact(null); return; }
                            patchContact.mutate({ id: c.id, patch });
                          }}
                          disabled={patchContact.isPending}
                          className="btn-primary text-xs px-2 py-0.5 disabled:opacity-50"
                        >
                          {patchContact.isPending ? 'Saving…' : 'Save'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-baseline gap-2">
                      <div className="flex-1">
                        <div className="font-medium">
                          {c.fullName ?? <span className="italic text-muted-foreground">unnamed</span>}
                          {c.role && <span className="ml-1.5 text-muted-foreground">· {c.role}</span>}
                        </div>
                        <div className="font-mono text-muted-foreground">
                          {c.email ?? '—'}
                          {c.phone && <span> · {c.phone}</span>}
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setEditContactDraft({
                            fullName: c.fullName ?? '', email: c.email ?? '',
                            phone: c.phone ?? '', role: c.role ?? '',
                          });
                          setEditingContact(c.id);
                        }}
                        className="text-primary hover:underline"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => deleteContact.mutate(c.id)}
                        className="text-muted-foreground hover:text-destructive"
                        aria-label="Remove contact"
                      >
                        ×
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {/* Top part numbers */}
      {topPartNumbers.length > 0 && (
        <div className="px-6 py-4 border-t border-border">
          <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
            Top part numbers (most quoted)
          </h3>
          <ul className="flex flex-wrap gap-2">
            {topPartNumbers.map((p) => (
              <li key={p.partNumber} className="text-xs px-2 py-1 rounded bg-muted">
                <span className="font-mono">{p.partNumber}</span>
                <span className="ml-1.5 text-muted-foreground">×{p.quotes}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Recent activity */}
      <div className="px-6 py-4 border-t border-border">
        <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
          Recent quotes ({recentActivity.length})
        </h3>
        {recentActivity.length === 0 ? (
          <div className="text-xs italic text-muted-foreground">No quotes yet.</div>
        ) : (
          <ul className="space-y-1">
            {recentActivity.map((r) => (
              <li
                key={r.vendorQuotePublicId}
                className="text-xs flex items-baseline justify-between gap-2 py-1 border-b border-border/40 last:border-0"
              >
                <div className="flex-1 truncate">
                  <span className="font-mono">{r.partNumber}</span>
                  <span className="text-muted-foreground"> · {r.rfqRef} #{r.lineNumber}</span>
                </div>
                <div className="font-mono">
                  {fmtMoney(r.unitPriceCents, r.currency)} × {r.qtyOffered}
                </div>
                <div className="text-muted-foreground">
                  {new Date(r.createdAt).toLocaleDateString()}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Notes */}
      <div className="px-6 py-4 border-t border-border">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs uppercase tracking-wide text-muted-foreground">Notes</h3>
          {editingNotes === null ? (
            <button
              onClick={() => setEditingNotes(vendor.notes ?? '')}
              className="text-xs font-medium text-primary hover:underline"
            >
              Edit
            </button>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={() => {
                  patchVendor.mutate({ notes: editingNotes });
                  setEditingNotes(null);
                }}
                className="text-xs font-medium text-primary hover:underline"
              >
                Save
              </button>
              <button
                onClick={() => setEditingNotes(null)}
                className="text-xs text-muted-foreground hover:underline"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
        {editingNotes !== null ? (
          <textarea
            value={editingNotes}
            onChange={(e) => setEditingNotes(e.target.value)}
            rows={4}
            className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
          />
        ) : (
          <div className="text-sm whitespace-pre-wrap text-muted-foreground">
            {vendor.notes || <span className="italic">No notes.</span>}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="border border-border rounded p-2 bg-background">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-sm font-semibold mt-0.5">{value}</div>
    </div>
  );
}
